import classNames from "classnames";
import { toast } from "extensions/toast";
import { isEqual } from "lodash-es";
import ReactDOMServer from "react-dom/server";
import { connect } from "react-redux";

import { getters } from "common/components/getters/getters";
import { Modal } from "common/components/modal";
import { pazienHistory } from "common/components/pazienHistory";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { captureException } from "extensions/sentry";

import {
    FETCH_BATCH_SUMMARY, SET_SORT_OPTIONS, TOGGLE_FLAGGED_STATUS, TOGGLE_RECONCILED_STATUS, UPDATE_RECON_QUALIFIERS
} from "common/actions/reconciliation";

import { SAVE_TABLE_CONFIG, UPDATE_STORED_CONFIG } from "common/actions/components";
import { references } from "common/helpers/references";
import { getQFilter, parseQualifiersForFilters } from "common/utils";

import {
    faFlag as farFlag,
    faPiggyBank as farPiggyBank
} from "@fortawesome/pro-regular-svg-icons";
import {
    faFlag as fasFlag,
    faPiggyBank as fasPiggyBank, faSortDown, faSortUp
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { BaseTable } from "merchant/features/analytics/common/transactions/transactionsTable";
import { table_mapper } from "./fields";

const mapStateToProps = (state) => ({
    bu_ref: state.common.bu_ref,
    startDate: state.common.date_range.startDate,
    endDate: state.common.date_range.endDate,
    section: state.reconciliation.section,
    transferStatus: state.reconciliation.transferStatus,
    sortOptions: state.reconciliation.sortOptions,
    qualifiers: state.reconciliation.qualifiers,
    table_configs: state.components.table_configs.recon_summary,
    has_oct: state.common.merchant_capabilities.has_oct,
    acquirers: state.common.merchant_dimensions.data.acquirer,
    currencies: state.common.merchant_dimensions.data.currency_alias
});

export class BatchTable extends BaseTable {
    constructor(props) {
        super(props);
        this.scrollPos = null;
    }

    componentDidMount() {
        this.fetchTableConfig();
        this.fetchData().then(this.enforceTableView);
    }

    componentDidUpdate(prevProps) {
        const webix_table = this.table_ref?.current?.table?.current?.ui;

        const needsConfigRefetch = this.props.transferStatus !== prevProps.transferStatus
            || this.props.section !== prevProps.section
            || !isEqual(this.props.sortOptions, prevProps.sortOptions);
        const needsDataRefetch = this.props.startDate !== prevProps.startDate
            || this.props.endDate !== prevProps.endDate
            || this.props.qualifiers !== prevProps.qualifiers
            || this.props.bu_ref !== prevProps.bu_ref;

        if (webix_table && !this.state.isLoading && this.scrollPos) {
            webix_table.scrollTo(this.scrollPos.x, 0);
            this.scrollPos = null;
        }

        if (!isEqual(this.props.sortOptions, prevProps.sortOptions) && webix_table) {
            this.scrollPos = webix_table.getScrollState();
        }

        if (needsConfigRefetch || needsDataRefetch) {
            if (needsConfigRefetch) {
                this.fetchTableConfig();
            } else {
                this.setTableUI();
            }
            this.fetchData({ page: 1 }).then(this.enforceTableView);
        }
    }

    fetchTableConfig = ({ section, transferStatus, table_configs } = this.props) => {
        let config = table_configs?.user?.[section] || table_configs?.pazien?.[section];
        if (config) {
            config = {
                ...config,
                data: config.data.reduce((acc, field) => {
                    if (transferStatus === "accepted" && field.getter_ref === "5ec7dc5375c262cf16068b15") {
                        return [...acc, {
                            ...field,
                            getter_ref: "5ec7dc5375c262cf16068b02"
                        }];
                    }

                    if (transferStatus === "settled" && field.getter_ref === "5ec7dc5375c262cf16068b02") {
                        return [...acc, {
                            ...field,
                            getter_ref: "5ec7dc5375c262cf16068b15"
                        }];
                    }

                    if (!this.props.has_oct && ["5ec7dc5375c262cf16068b08", "5ec7dc5375c262cf16068b07", "5ec7dc5375c262cf16068b0c", "5f8d98ba78617a51ac2acf22"].includes(field.getter_ref)) {
                        return acc;
                    }
                    return [...acc, field];
                }, [])
            };

            this.setState({ config }, this.setTableUI);
        } else {
            const error = new Error("Table configs did not load");
            this.setState({ hasError: true, error: { error, capture: true } });
        }
    };

    fetchData = ({ ...options } = {}) => {
        this.cleanPromises();
        this.setState({ isLoading: true });
        const promise = FETCH_BATCH_SUMMARY({
            section: this.props.section,
            start: this.props.startDate,
            end: this.props.endDate,
            status: this.props.transferStatus,
            pivots: ["stl_currency", this.props.dateType, "acquirer", "funds_transfer_groupings", "acquiring_entity"],
            limit: 100,
            sort: this.props.sortOptions.key,
            order: this.props.sortOptions.order,
            page: this.state.page || 1,
            paginate: true,
            q: this.props.qualifiers,
            ...options
        }).then(
            ({ results, ...rest }) => {
                this.setState({
                    isLoading: false, hasError: false, data: results, ...rest
                });
                this.error = null;

                return results;
            }
        ).catch(this.catchFetchErrors);

        this.promises.push(promise);

        return promise;
    };

    getDrilldownAnchor = (formatFn, section) => (obj) => {
        const q = getQFilter({
            keys: [
                "stl_currency",
                this.props.dateType,
                "acquirer",
                "funds_transfer_groupings.merchant_id",
                "funds_transfer_groupings.company_id",
                "funds_transfer_groupings.batch_id",
                "funds_transfer_groupings.bank_statement_narrative",
                "funds_transfer_groupings.store_id"
            ],
            obj
        });
        const displayOptions = getQFilter({
            keys: ["view", "section", "transferStatus"],
            obj: {
                transferStatus: this.props.transferStatus,
                view: "txns",
                section
            }
        });

        return ReactDOMServer.renderToStaticMarkup(
            <a href={pazienHistory.addParamsToFragment(`/merchant/reconciliation3/${displayOptions}`, { q })} className="drilldown">{formatFn(obj)}</a>
        );
    };

    getPazienSelectFilterHeader = ({ getter, extraParams = {}, customOptions = false }) => [{
        content: "pazienSelectFilter",
        separator: "|",
        inputConfig: {
            label: getter.name,
            separator: "|"
        },
        onChange(master, table, self) {
            master.filterByAll();

            const filterValue = self.value;

            const newQualifiersFragment = filterValue ? `${getter.key}:${Qualifiers.normalizeValue(filterValue.split("|"))}` : "";

            UPDATE_RECON_QUALIFIERS(
                newQualifiersFragment,
                [getter.key]
            );
        },
        template(obj) {
            return {
                ...obj,
                value: references.toAlias(obj.value, getter._id)
            };
        },
        suggest: {
            fitMaster: false,
            selectAll: true,
            sortOptions: true,
            width: 300
        },
        value: parseQualifiersForFilters({
            value: this.props.qualifiers,
            categories: [getter.key],
            customOptions
        }),
        ...extraParams
    }];

    getColumnsFromConfig = ({ forceFullView = false } = {}) => this.state.config.data
        .map(table_mapper(forceFullView))
        .map((column, index, arr) => {
            const getter = getters.findById(column.getter_ref);
            const { preFormat } = column;

            if (column.cellOptions.drilldown) {
                column.template = this.getDrilldownAnchor(preFormat, column.section_ref || this.props.section);
            } else if (column.getter_ref === "5b19997edabe69133a95efb2") {
                column.template = (obj) => (
                    <div className="ftg flex-content">
                        <div>{obj?.["funds_transfer_groupings.company_id"] || obj["funds_transfer_groupings.merchant_id"]}</div>
                        {obj["funds_transfer_groupings.bank_statement_narrative"] && <div>{obj["funds_transfer_groupings.bank_statement_narrative"]}</div>}
                        {obj["funds_transfer_groupings.batch_id"] && <div>{obj["funds_transfer_groupings.batch_id"]}</div>}
                        {obj["funds_transfer_groupings.store_id"] && <div>{obj["funds_transfer_groupings.store_id"]}</div>}
                    </div>
                );
            } else if (getter?.valueType === "amount") {
                column.template = (obj) => {
                    const value = preFormat(obj).split(" ");
                    return (
                        <div className="flex-content">
                            <span className="amount">{value[0]}</span>&nbsp;
                            <span className="currency">{value[1]}</span>
                        </div>
                    );
                };
            } else {
                column.template = preFormat;
            }

            let header = {
                text: ReactDOMServer.renderToStaticMarkup(
                    <div className="flex-content" data-getter-ref={column.getter_ref}>
                        <div className="header">{getter?.name}</div>
                    </div>
                ),
                tooltip: column.headerTooltip,
                css: classNames(column.css, { sortable: column.sortOptions.key, bold: column.cellOptions.bold })
            };

            if (column.sortOptions.key === this.props.sortOptions.key) {
                Object.assign(header, {
                    text: ReactDOMServer.renderToStaticMarkup(
                        <div className="flex-content">
                            <div className="header">
                                <div>{getter.name}</div>
                                <div className="sort-indicator">
                                    <FontAwesomeIcon icon={this.props.sortOptions.order === 1 ? faSortUp : faSortDown}/>
                                </div>
                            </div>
                        </div>
                    ),
                    tooltip: `${column.headerTooltip} (${this.props.sortOptions.order === 1 ? "ASC" : "DESC"})`
                });
            }

            if (column.getter_ref === "5db9d5a782d84c6c839df36f") {
                header = this.getPazienSelectFilterHeader({
                    getter,
                    extraParams: {
                        options: [...this.props.acquirers]
                    },
                    customOptions: false
                });
            }

            if (column.getter_ref === "5b19997edabe69133a95efb1") {
                header = this.getPazienSelectFilterHeader({
                    getter,
                    extraParams: {
                        options: [...this.props.currencies]
                    },
                    customOptions: false
                });
            }

            return {
                ...column,
                header,
                tooltip: false,
                adjust: true,
                css: classNames(column.css)
            };
        });

    getExportOptions = () => {};

    getSelectedSummaryFilters = () => {
        const webix = this.table_ref.current?.table?.current?.ui;
        if (webix) {
            const selected = [];
            
            webix.eachRow((rowID) => {
                const obj = webix.getItem(rowID);
                if (obj.ch1) {
                    selected.push(obj);
                }
            }, true);
            
            if (webix.data.count() === selected.length) {
                return [];
            }

            return selected.map((obj) => getQFilter({
                keys: [
                    "stl_currency",
                    this.props.dateType,
                    "acquirer",
                    "funds_transfer_groupings.merchant_id",
                    "funds_transfer_groupings.company_id",
                    "funds_transfer_groupings.batch_id",
                    "funds_transfer_groupings.bank_statement_narrative",
                    "funds_transfer_groupings.store_id"
                ],
                obj
            }));
        }
        return [];
    };

    handleConfigSave = (config) => {
        SAVE_TABLE_CONFIG(config)
            .then((res) => { UPDATE_STORED_CONFIG(res); this.setState({ config: res, fullView: false }); })
            .then(this.updateColumns)
            .then(this.enforceTableView)
            .then(this.hideConfig)
            .catch((error) => { toast.error("There was a problem saving your config. Try again later."); captureException(error); });
    };

    getUiOptions = () => ({
        scrollX: true,
        minHeight: 330,
        rowHeight: 70,
        rowLineHeight: 70,
        hover: "row-hover",
        tooltip: {
            dx: 10,
            dy: -40
        },
        onClick: {
            drilldown: this.actionWrapper(({ id, obj, columnConfig }) => {
                this.drilldownToBatch({ obj, columnConfig });
            }, true),
            flagged: this.actionWrapper(
                ({ id, obj, webix }) => TOGGLE_FLAGGED_STATUS({ obj }).then(
                    () => webix.updateItem(id, { ...obj, flagged: !obj.flagged })
                ).catch((error) => captureException(error))
            ),
            reconciled: this.actionWrapper(
                ({ id, obj, webix }) => TOGGLE_RECONCILED_STATUS({ obj }).then(
                    () => webix.updateItem(id, { ...obj, reconciled: !obj.reconciled })
                ).catch((error) => captureException(error))
            ),
            webix_cell: this.actionWrapper(({ obj, columnConfig }) => {
                if (this.props.section !== "all") this.drilldownToBatch({ obj, columnConfig });
            }),
            sortable: this.actionWrapper(({ columnConfig }) => {
                const newOptions = {};
                if (columnConfig.sortOptions.key !== this.props.sortOptions.key) {
                    Object.assign(newOptions, { key: columnConfig.sortOptions.key });
                } else {
                    Object.assign(newOptions, { order: this.props.sortOptions.order === 1 ? -1 : 1 });
                }
                SET_SORT_OPTIONS({ ...this.props.sortOptions, ...newOptions });
            })
        }
    });

    getOnEvents = (self = this) => ({
        onAfterLoad() {
            if (self.state.fullView) {
                this.showColumnBatch("expanded", true); // show "expanded", preserve state of others
            } else {
                this.showColumnBatch("expanded", false); // hide "expanded", preserve state of others
            }
        }
    });

    setTableUI = () => {
        this.ui = this.getWebixConf({
            noDataErrorProps: {
                header: "No data to display.",
                message: "We couldn't find any records for your request"
            }
        });
    };

    getControlColumn = () => [
        {
            id: "ch1",
            width: 40,
            header: { content: "masterCheckbox", tooltip: "Select all" },
            template: "<div>{common.checkbox()}</div>",
            tooltip: false
        },
        {
            id: "control",
            header: this.getPazienSelectFilterHeader({
                getter: {
                    name: "Status"
                },
                extraParams: {
                    options: ["Flagged", "Reconciled"], // custom Options
                    onChange(master, table, self) {
                        const categories = ["flagged", "reconciled"];
                        master.filterByAll();

                        const filterValue = self.value.toLowerCase();
                        const newQualifiersFragment = categories
                            .filter((c) => filterValue.includes(c))
                            .map((c) => `${c}:true`)
                            .join(" ");
                        UPDATE_RECON_QUALIFIERS(
                            newQualifiersFragment,
                            categories
                        );
                    },
                    // Need to disable generic webix filtering in this column because
                    // custom filter value does not match data and results in empty table.
                    // Data is already filtered by api, so we just forcing webix to show it all
                    compare() { return true; },
                    value: parseQualifiersForFilters({
                        value: this.props.qualifiers,
                        categories: ["flagged", "reconciled"],
                        customOptions: true
                    }),
                    suggest: {
                        fitMaster: false,
                        selectAll: true,
                        sortOptions: false,
                        width: 300
                    }
                },
                customOptions: true
            }),
            adjust: true,
            css: "control-column",
            tooltip: false,
            minWidth: 100,
            template: (obj) => (
                <div className="flex-content">
                    <div className="controls">
                        <div className={classNames("control-icon", "flagged", `status-${obj.flagged}`)}>
                            <FontAwesomeIcon size="lg" icon={obj.flagged ? fasFlag : farFlag}/>
                        </div>
                        <div className={classNames("control-icon", "reconciled", `status-${obj.reconciled}`)}>
                            <FontAwesomeIcon size="lg" icon={obj.reconciled ? fasPiggyBank : farPiggyBank}/>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    drilldownToBatch = ({ obj, columnConfig }) => {
        const q = getQFilter({
            keys: [
                "stl_currency",
                this.props.dateType,
                "acquirer",
                "funds_transfer_groupings.merchant_id",
                "funds_transfer_groupings.company_id",
                "funds_transfer_groupings.batch_id",
                "funds_transfer_groupings.bank_statement_narrative",
                "funds_transfer_groupings.store_id"
            ],
            obj
        });
        this.props.navigate({
            display: {
                view: "txns",
                section: columnConfig?.section_ref || this.props.section
            },
            filters: q
        });
    };

    render() {
        const { page, count, next, isLoading } = this.state;
        return (
            <div className={classNames("configurable-table-view", this.props.className, this.props.section)}>
                <Modal
                    close={this.hideConfig}
                    header="Table options"
                    portalId="modal-batch-table-configure"
                    show={this.state.configVisible}
                >
                    {this.renderConfig()}
                </Modal>
                {this.props.showControls && this.renderControls()}
                {this.state.isLoading ? <BaseTable.TablePlaceholder columns={5} rows={2} /> : <BaseTable.WebixDatatable className={classNames("configurable-table", this.props.className)} ref={this.table_ref} ui={this.ui} data={this.state.data} />}
                {this.props.paginatorBottom
                    && (
                    <BaseTable.Paginator
                        page={page}
                        count={count}
                        next={next}
                        loading={isLoading}
                        limit={100}
                        onPageChange={this.pageHandler}
                        results_length={this.state.data.length}
                    />
                    )}
            </div>
        );
    }
}

// eslint-disable-next-line react-redux/connect-prefer-named-arguments
export const BatchTableRedux = connect(mapStateToProps, null, null, { forwardRef: true })(BatchTable);
